/**
 * Celebrations Tab - Win celebration customization
 */

import type { CelebrationConfig } from '../../../public/deals/components/slot-machine/themes/skin-types';

interface CelebrationsTabProps {
  config: CelebrationConfig;
  onUpdate: (config: CelebrationConfig) => void;
}

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 p-1 border border-gray-300 rounded cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
        />
      </div>
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  suffix?: string;
  min?: number;
  max?: number;
}

function NumberField({ label, value, onChange, suffix, min, max }: NumberFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
      </div>
    </div>
  );
}

interface ParticleColorsFieldProps {
  label: string;
  colors: string[];
  onChange: (colors: string[]) => void;
}

function ParticleColorsField({ label, colors, onChange }: ParticleColorsFieldProps) {
  const updateColor = (index: number, color: string) => {
    const newColors = [...colors];
    newColors[index] = color;
    onChange(newColors);
  };

  const addColor = () => {
    onChange([...colors, '#ffffff']);
  };

  const removeColor = (index: number) => {
    if (colors.length > 1) {
      onChange(colors.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {colors.map((color, index) => (
          <div key={index} className="flex items-center gap-1">
            <input
              type="color"
              value={color}
              onChange={(e) => updateColor(index, e.target.value)}
              className="w-8 h-8 p-0.5 border border-gray-300 rounded cursor-pointer"
            />
            {colors.length > 1 && (
              <button
                onClick={() => removeColor(index)}
                className="text-gray-400 hover:text-red-500 text-xs"
              >
                x
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addColor}
          className="w-8 h-8 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-primary-500 hover:text-primary-500"
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function CelebrationsTab({ config, onUpdate }: CelebrationsTabProps) {
  const updateField = <K extends keyof CelebrationConfig>(field: K, value: CelebrationConfig[K]) => {
    onUpdate({ ...config, [field]: value });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-4">Win Celebrations</h2>
        <p className="text-sm text-gray-500 mb-6">
          Customize how wins are celebrated. Configure thresholds, labels, colors, and animations.
        </p>
      </div>

      {/* Win Thresholds */}
      <div>
        <h3 className="text-md font-medium mb-4">Win Thresholds</h3>
        <p className="text-sm text-gray-500 mb-4">
          Set the multiplier thresholds that trigger different celebration tiers.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="Big Win Threshold"
            value={config.bigWinThreshold}
            onChange={(v) => updateField('bigWinThreshold', v)}
            suffix="x bet"
            min={1}
          />
          <NumberField
            label="Mega Win Threshold"
            value={config.megaWinThreshold}
            onChange={(v) => updateField('megaWinThreshold', v)}
            suffix="x bet"
            min={1}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Jackpot celebrations are triggered by hitting the jackpot symbol combination.
        </p>
      </div>

      {/* Win Tier Labels */}
      <div>
        <h3 className="text-md font-medium mb-4">Win Tier Labels</h3>
        <p className="text-sm text-gray-500 mb-4">
          Customize the text displayed for each win tier.
        </p>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Big Win Label</label>
            <input
              type="text"
              value={config.bigWinLabel}
              onChange={(e) => updateField('bigWinLabel', e.target.value)}
              placeholder="BIG WIN!"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mega Win Label</label>
            <input
              type="text"
              value={config.megaWinLabel}
              onChange={(e) => updateField('megaWinLabel', e.target.value)}
              placeholder="MEGA WIN!"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jackpot Label</label>
            <input
              type="text"
              value={config.jackpotLabel}
              onChange={(e) => updateField('jackpotLabel', e.target.value)}
              placeholder="JACKPOT!"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Typography */}
      <div>
        <h3 className="text-md font-medium mb-4">Typography</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Win Amount Font</label>
            <input
              type="text"
              value={config.winAmountFont || ''}
              onChange={(e) => updateField('winAmountFont', e.target.value)}
              placeholder="Inter, sans-serif"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Win Label Font</label>
            <input
              type="text"
              value={config.winLabelFont || ''}
              onChange={(e) => updateField('winLabelFont', e.target.value)}
              placeholder="Inter, sans-serif"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Colors */}
      <div>
        <h3 className="text-md font-medium mb-4">Win Tier Colors</h3>
        <div className="grid grid-cols-3 gap-4">
          <ColorField
            label="Big Win Color"
            value={config.bigWinColor}
            onChange={(v) => updateField('bigWinColor', v)}
          />
          <ColorField
            label="Mega Win Color"
            value={config.megaWinColor}
            onChange={(v) => updateField('megaWinColor', v)}
          />
          <ColorField
            label="Jackpot Color"
            value={config.jackpotColor}
            onChange={(v) => updateField('jackpotColor', v)}
          />
        </div>
      </div>

      {/* Particle Colors */}
      <div>
        <h3 className="text-md font-medium mb-4">Particle Effects</h3>
        <p className="text-sm text-gray-500 mb-4">
          Set the colors for the particle effects that appear during celebrations.
        </p>
        <div className="space-y-4">
          <ParticleColorsField
            label="Big Win Particles"
            colors={config.bigWinParticles || ['#22c55e', '#4ade80', '#86efac']}
            onChange={(colors) => updateField('bigWinParticles', colors)}
          />
          <ParticleColorsField
            label="Mega Win Particles"
            colors={config.megaWinParticles || ['#eab308', '#facc15', '#fde047']}
            onChange={(colors) => updateField('megaWinParticles', colors)}
          />
          <ParticleColorsField
            label="Jackpot Particles"
            colors={config.jackpotParticles || ['#f97316', '#fb923c', '#fdba74']}
            onChange={(colors) => updateField('jackpotParticles', colors)}
          />
        </div>
      </div>

      {/* Animation Timing */}
      <div>
        <h3 className="text-md font-medium mb-4">Animation Timing</h3>
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="Count Up Duration"
            value={config.countUpDuration}
            onChange={(v) => updateField('countUpDuration', v)}
            suffix="ms"
            min={500}
            max={10000}
          />
          <NumberField
            label="Display Duration"
            value={config.displayDuration}
            onChange={(v) => updateField('displayDuration', v)}
            suffix="ms"
            min={1000}
            max={15000}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Count up duration controls how long it takes to animate the win amount.
          Display duration controls how long the celebration stays on screen.
        </p>
      </div>

      {/* Preview */}
      <div>
        <h3 className="text-md font-medium mb-4">Preview</h3>
        <div className="bg-gray-900 rounded-lg p-8 text-center space-y-6">
          {/* Big Win Preview */}
          <div>
            <p
              className="text-2xl font-bold mb-1"
              style={{ color: config.bigWinColor, fontFamily: config.winLabelFont }}
            >
              {config.bigWinLabel}
            </p>
            <p
              className="text-4xl font-bold"
              style={{ color: config.bigWinColor, fontFamily: config.winAmountFont }}
            >
              $1,234.56
            </p>
            <div className="flex justify-center gap-2 mt-2">
              {(config.bigWinParticles || []).map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Mega Win Preview */}
          <div className="pt-4 border-t border-gray-700">
            <p
              className="text-3xl font-bold mb-1"
              style={{ color: config.megaWinColor, fontFamily: config.winLabelFont }}
            >
              {config.megaWinLabel}
            </p>
            <p
              className="text-5xl font-bold"
              style={{ color: config.megaWinColor, fontFamily: config.winAmountFont }}
            >
              $12,345.67
            </p>
            <div className="flex justify-center gap-2 mt-2">
              {(config.megaWinParticles || []).map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Jackpot Preview */}
          <div className="pt-4 border-t border-gray-700">
            <p
              className="text-4xl font-bold mb-1"
              style={{ color: config.jackpotColor, fontFamily: config.winLabelFont }}
            >
              {config.jackpotLabel}
            </p>
            <p
              className="text-6xl font-bold"
              style={{ color: config.jackpotColor, fontFamily: config.winAmountFont }}
            >
              $123,456.78
            </p>
            <div className="flex justify-center gap-2 mt-2">
              {(config.jackpotParticles || []).map((color, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
